// @ts-strict-ignore
/* eslint-disable no-prototype-builtins */
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Offer } from 'rainbow-web-sdk/models/offer.model';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { Service } from 'rainbow-web-sdk/services/service';
import xmlParser from 'rainbow-web-sdk/utils/xmlParser';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';

export const PROFILE_SVC = 'ProfileService';

/**
 * Description: Some bot or process needs a permanent authentication even though the account authentication changes.
 * Managing the full JWT authentication process is not relevant on low power devices (iOT,...).
 * In those cases, we can use API_KEYS instead;
 * This is the Interface of the SDK API Key
 */
export interface SdkApiKey {
    // the API key that is only visible on CREATE
    apiKey?: string;

    //The identifier of the API_KEY
    id?: string;

    //The scope of the API_KEY
    scope: string[];

    //The description of the API_KEY
    description: string;

    //Indicate if the API_KEY is active
    isActive: boolean;

    //The identifier of the API_KEY owner
    userId?: string;

    //The Email of the API_KEY owner
    loginEmail?: string;

    //The expiration date of the API_KEY
    expirationDate: string;
}

export enum ProfileFeatures {
    COMPANY_ADMIN_COUNT = "COMPANY_ADMIN_COUNT",
    COMPANY_LOGO_MODIFICATION = "COMPANY_LOGO_MODIFICATION",
    COMPANY_DOMAIN_NAME_MODIFICATION = "COMPANY_DOMAIN_NAME_MODIFICATION",
    COMPANY_DETAILS_MODIFICATION = "COMPANY_DETAILS_MODIFICATION",
    COMPANY_SINGLE_SIGN_ON_SAML = "COMPANY_SINGLE_SIGN_ON_SAML",
    COMPANY_SINGLE_SIGN_ON_OIDC = "COMPANY_SINGLE_SIGN_ON_OIDC",
    SECURITY_MFA_TOTP = "SECURITY_MFA_TOTP",
    WEBRTC_FOR_MOBILE = "WEBRTC_FOR_MOBILE",
    BUBBLE_PARTICIPANT_COUNT = "BUBBLE_PARTICIPANT_COUNT",
    TELEPHONY_BASIC_CALL = "TELEPHONY_BASIC_CALL",
    TELEPHONY_SECOND_CALL = "TELEPHONY_SECOND_CALL",
    TELEPHONY_TRANSFER_CALL = "TELEPHONY_TRANSFER_CALL",
    TELEPHONY_CONFERENCE_CALL = "TELEPHONY_CONFERENCE_CALL",
    TELEPHONY_DEFLECT_CALL = "TELEPHONY_DEFLECT_CALL",
    TELEPHONY_PHONE_BOOK = "TELEPHONY_PHONE_BOOK",
    TELEPHONY_VOICE_MAIL = "TELEPHONY_VOICE_MAIL",
    TELEPHONY_CALL_FORWARD = "TELEPHONY_CALL_FORWARD",
    TELEPHONY_NOMADIC = "TELEPHONY_NOMADIC",
    CONFERENCE_PARTICIPANT_COUNT = "CONFERENCE_PARTICIPANT_COUNT",
    WEBRTC_CONFERENCE_ALLOWED = "WEBRTC_CONFERENCE_ALLOWED",
    WEBRTC_CONFERENCE_PARTICIPANT_COUNT = "WEBRTC_CONFERENCE_PARTICIPANT_COUNT",
    WEBRTC_PARTICIPANT_ALLOWED = "WEBRTC_PARTICIPANT_ALLOWED",
    CONFERENCE_ALLOWED = "CONFERENCE_ALLOWED",
    CONFERENCE_DIAL_OUT = "CONFERENCE_DIAL_OUT",
    CONFERENCE_RECORDING = "CONFERENCE_RECORDING",
    MSO365_CALENDAR_PRESENCE = "MSO365_CALENDAR_PRESENCE",
    MSO365_DIRECTORY_PROVISIONING = "MSO365_DIRECTORY_PROVISIONING",
    MSO365_DIRECTORY_SEARCH = "MSO365_DIRECTORY_SEARCH",
    MSO365_CONTACT_SEARCH = "MSO365_CONTACT_SEARCH",
    MS_OUTLOOK_PLUGIN = "MS_OUTLOOK_PLUGIN",
    MS_SKYPE_PLUGIN = "MS_SKYPE_PLUGIN",
    FILE_SHARING_QUOTA_GB = "FILE_SHARING_QUOTA_GB",
    GOOGLE_CALENDAR_PRESENCE = "GOOGLE_CALENDAR_PRESENCE",
    WEBRTC_P2P_RECORDING = "WEBRTC_P2P_RECORDING",
    BUBBLE_PROMOTE_MEMBER = "BUBBLE_PROMOTE_MEMBER",
    BUBBLE_GUESTS_ALLOWED = "BUBBLE_GUESTS_ALLOWED",
    BUBBLE_ACTIVE_SPEAKER = "BUBBLE_ACTIVE_SPEAKER",
    TELEPHONY_WEBRTC_GATEWAY = "TELEPHONY_WEBRTC_GATEWAY",
    TELEPHONY_WEBRTC_PSTN_CALLING = "TELEPHONY_WEBRTC_PSTN_CALLING",
    ANALYTICS_DASHBOARD_EC = "ANALYTICS_DASHBOARD_EC",
    ANALYTICS_DASHBOARD_BP = "ANALYTICS_DASHBOARD_BP",
    TELEPHONY_CALL_SUBJECT = "CALL_SUBJECT",
    CHANNEL_CREATE = "CHANNEL_CREATE",
    CHANNEL_CREATE_ADMIN_ROLE_BYPASS = "CHANNEL_CREATE_ADMIN_ROLE_BYPASS",
    CHANNEL_ACTIVATED = "CHANNEL_ACTIVATED",
    MANAGE_ANDROID_TV = "MANAGE_ANDROID_TV",
    USE_ANDROID_TV = "USE_ANDROID_TV",
    LDAP_DIRECTORY_SYNC = "LDAP_DIRECTORY_SYNC",
    LDAP_CONTACT_SYNC = "LDAP_CONTACT_SYNC",
    MASSPRO_USER_PROVISIONING_CSV = "MASSPRO_USER_PROVISIONING_CSV",
    SEARCH_BY_TAGS = "SEARCH_BY_TAGS",
    TELEPHONY_3DPARTY_ASSOC = "TELEPHONY_3DPARTY_ASSOC",
    WEBCLIENT_UI_2_0 = "WEBCLIENT_UI_2_0",
    WEBRTC_UNIFIED_PLAN = "WEBRTC_UNIFIED_PLAN",
    WEBRTC_CONFERENCE_SIMULCAST = "WEBRTC_CONFERENCE_SIMULCAST",
    USE_CLOUD_PBX = "USE_CLOUD_PBX",
    USE_OXO_MANAGED_PBX = "USE_OXO_MANAGED_PBX",
    SOFTPHONE_MODE_ONLY = "SOFTPHONE_MODE_ONLY",
    GIVE_REQUEST_REMOTE_CONTROL = "GIVE_REQUEST_REMOTE_CONTROL",
    BUBBLE_MAX_LOAD_STARTUP = "BUBBLE_MAX_LOAD_STARTUP",
    BUBBLE_COUNT = "BUBBLE_COUNT",
    BUBBLE_MEETING_SCHEDULER = "BUBBLE_MEETING_SCHEDULER",
    BUBBLE_TALKING_TIME = "BUBBLE_TALKING_TIME",
    APP_PERMISSIONS = "APP_PERMISSIONS",
    FREE_UI_CUSTOMIZATION = "FREE_UI_CUSTOMIZATION",
    PREMIUM_UI_CUSTOMIZATION = "PREMIUM_UI_CUSTOMIZATION",
    TELEPHONY_MAKE_CALL_CTI_APP = "TELEPHONY_MAKE_CALL_CTI_APP",
    CLOUD_PBX_WELCOME = "CLOUD_PBX_WELCOME",
    BUBBLE_TAG_COUNT = "BUBBLE_TAG_COUNT",
    TELEPHONY_CCD_AGENT = "TELEPHONY_CCD_AGENT",
    ALERT_NOTIFICATIONS_ALLOWED = "ALERT_NOTIFICATIONS_ALLOWED",
    BUBBLE_FOLDER_COUNT = "BUBBLE_FOLDER_COUNT",
    BUBBLE_POP_OUT_VIDEO_SHARING = "BUBBLE_POP_OUT_VIDEO_SHARING",
    ACCESSIBILITY_KEYBOARD_SHORTCUTS = "ACCESSIBILITY_KEYBOARD_SHORTCUTS",
    WEBRTC_ADD_PARTICIPANT = "WEBRTC_ADD_PARTICIPANT",
    WEBINAR_ACCESS = "WEBINAR_ACCESS",
    WEBINAR_MAX_ORGANIZERS = "WEBINAR_MAX_ORGANIZERS",
    WEBINAR_MAX_PARTICIPANTS = "WEBINAR_MAX_PARTICIPANTS",
    WEBINAR_EXTENDED_BACKSTAGE = "WEBINAR_EXTENDED_BACKSTAGE",
    BLURRING_UI_2_0 = "BLURRING_UI_2_0",
    TELEPHONY_MAX_KEYS = "TELEPHONY_MAX_KEYS",
    TELEPHONY_MAX_SPEED_DIALING_KEYS = "TELEPHONY_MAX_SPEED_DIALING_KEYS",
    TELEPHONY_MAX_FEATURE_CODE_KEYS = "TELEPHONY_MAX_FEATURE_CODE_KEYS",
    TELEPHONY_MAX_SUPERVISION_KEYS = "TELEPHONY_MAX_SUPERVISION_KEYS",
    CONNECTIVITY_ALL_USER = "CONNECTIVITY_ALL_USER",
    WEBRTC_BLIND_TRANSFER_CALL = "WEBRTC_BLIND_TRANSFER_CALL",
    TELEPHONY_ADD_PARTICIPANT = "TELEPHONY_ADD_PARTICIPANT",
    TELEPHONY_BASIC_ATTENDANT_CONSOLE = "TELEPHONY_BASIC_ATTENDANT_CONSOLE",
    TELEPHONY_HYBRID_ATTENDANT_CONSOLE = "TELEPHONY_HYBRID_ATTENDANT_CONSOLE",
    MIX_WEBRTC_PSTN_CALLS = "MIX_WEBRTC_PSTN_CALLS",
    CLOUD_PBX_PERSONAL_ROUTINES = "CLOUD_PBX_PERSONAL_ROUTINES",
    TELEPHONY_MULTI_WEBRTC_GATEWAY = "TELEPHONY_MULTI_WEBRTC_GATEWAY",
    WEBRTC_GRID49 = "WEBRTC_GRID49",
    WEBRTC_CONFERENCE_V2_MODEL = "WEBRTC_CONFERENCE_V2_MODEL",
    TELEPHONY_OXO_MULTICALL = "TELEPHONY_OXO_MULTICALL",
    WEBRTC_MODULATE_VIDEO_QUALITY = "WEBRTC_MODULATE_VIDEO_QUALITY",
    ADMIN_ALARMS = "ADMIN_ALARMS",
    FLEXIBLE_PANEL = "FLEXIBLE_PANEL",
    BUBBLE_PSTN_DIALIN = "BUBBLE_PSTN_DIALIN",
    CONFERENCE_RECORDING_MAX_TIMER = "CONFERENCE_RECORDING_MAX_TIMER",
    PERSONAL_DIRECTORY = "PERSONAL_DIRECTORY",
    MSO365_TEAMS_PRESENCE = "MSO365_TEAMS_PRESENCE",
    ELEARNING_TRAINING = "ELEARNING_TRAINING",
    REPORT_A_PROBLEM = "REPORT_A_PROBLEM",
    WEBRTC_MANAGE_TWO_CALLS = "WEBRTC_MANAGE_TWO_CALLS",
    SUPERVISION_GROUP_MAX_NUMBER = "SUPERVISION_GROUP_MAX_NUMBER",
    SUPERVISION_GROUP_MAX_USERS = "SUPERVISION_GROUP_MAX_USERS",
    SUPERVISION_GROUP_SUPERVISOR_ALLOWED = "SUPERVISION_GROUP_SUPERVISOR_ALLOWED",
    SUPERVISION_GROUP_IN_OUT_ALLOWED = "SUPERVISION_GROUP_IN_OUT_ALLOWED",
    SUPERVISION_GROUP_SHOW_CONSOLE = "SUPERVISION_GROUP_SHOW_CONSOLE",
    TELEPHONY_CALL_POPOUT = "TELEPHONY_CALL_POPOUT",
    TELEPHONY_OXE_EMERGENCY = "TELEPHONY_OXE_EMERGENCY",
    MESSAGE_BOOKMARK = "MESSAGE_BOOKMARK",
    CLOUD_PBX_CALL_QUEUE_MAX_USERS = "CLOUD_PBX_CALL_QUEUE_MAX_USERS",
    CLOUD_PBX_MANAGER_ASSISTANT = "CLOUD_PBX_MANAGER_ASSISTANT",
    TELEPHONY_JOIN_BUBBLE = "TELEPHONY_JOIN_BUBBLE",
    TO_DO_LIST_MAX_ITEMS = "TO_DO_LIST_MAX_ITEMS",
    RED_ACCESS = "RED_ACCESS",
    COMPANY_DIRECTORY_INTERNAL_NUM = "COMPANY_DIRECTORY_INTERNAL_NUM",
    COMPANY_DIRECTORY_SHORTCODE = "COMPANY_DIRECTORY_SHORTCODE",
    TELEPHONY_SOFTPHONE_NOT_ALLOWED = "TELEPHONY_SOFTPHONE_NOT_ALLOWED",
    TELEPHONY_OXE_OVERFLOW = "TELEPHONY_OXE_OVERFLOW",
    CAPABILITY_OXE_OVERFLOW = "CAPABILITY_OXE_OVERFLOW",
    FILE_SHARING_EXTERNAL_OWNCLOUD = "FILE_SHARING_EXTERNAL_OWNCLOUD",
    AUDIO_NOISE_REDUCTION = "AUDIO_NOISE_REDUCTION",
    MIX_WEBRTC_PSTN_CALLS_FOR_PBX = "MIX_WEBRTC_PSTN_CALLS_FOR_PBX",
    WEBRTC_CALL_SHIFT = "WEBRTC_CALL_SHIFT",
    TELEPHONY_OXE_SECRET = "TELEPHONY_OXE_SECRET",
    TELEPHONY_SOFTPHONE_V3 = "TELEPHONY_SOFTPHONE_V3",
    TELEPHONY_SOFTPHONE_V3_UC = "TELEPHONY_SOFTPHONE_V3_UC",
    CLOUD_PBX_CALL_QUEUE_RECORDING = "CLOUD_PBX_CALL_QUEUE_RECORDING",
    OPEN_XCHANGE_CALENDAR_PRESENCE = "OPEN_XCHANGE_CALENDAR_PRESENCE",
    HYBRID_MANAGER_ASSISTANT = "HYBRID_MANAGER_ASSISTANT",
    BLUE_GREEN_WEB = "BLUE_GREEN_WEB",
    BUBBLE_POLLING = "BUBBLE_POLLING",
    RAISE_HAND_CONFERENCE = "RAISE_HAND_CONFERENCE",
    PROMOTE_ALL = "PROMOTE_ALL",
    TELEPHONY_MAX_CALL = "TELEPHONY_MAX_CALL",
    DESKTOP_EXTEND_BUTTON = "DESKTOP_EXTEND_BUTTON",
    CALL_DURING_BUBBLE = "CALL_DURING_BUBBLE",
    CONFERENCE_ATTENDANCE = "CONFERENCE_ATTENDANCE",
    PIN_MESSAGES = "PIN_MESSAGES",
    PINS_ITEMS_COUNT_PER_CONVERSATION = "PINS_ITEMS_COUNT_PER_CONVERSATION",
    TELEPHONY_CALL_BUSY = "TELEPHONY_CALL_BUSY",
    LOWER_ALL_HANDS = "LOWER_ALL_HANDS",
    CLOUD_PBX_DECT_ZERO_TOUCH = "CLOUD_PBX_DECT_ZERO_TOUCH",
    CLOUD_PBX_PHONES_GETLOGS = "CLOUD_PBX_PHONES_GETLOGS",
    SECURITY_PASSWORDLESS = "SECURITY_PASSWORDLESS",
    CLOUD_PBX_PHONES_GROUP_WITHDRAW = "CLOUD_PBX_PHONES_GROUP_WITHDRAW",
    BUBBLE_WAITING_ROOM = "BUBBLE_WAITING_ROOM",
    BLURRING_UI_3_0 = "BLURRING_UI_3_0",
    MASK_COMPANY_SEARCH = "MASK_COMPANY_SEARCH",
    CLEAR_CHAT = "CLEAR_CHAT",
    TELEPHONY_OXE_EMERGENCY_SITE = "TELEPHONY_OXE_EMERGENCY_SITE",
    TELEPHONY_CCD_AGENT_PROFILE = "TELEPHONY_CCD_AGENT_PROFILE",
    TELEPHONY_HYBRID_SOFTPHONE_CALL_SHIFT = "TELEPHONY_HYBRID_SOFTPHONE_CALL_SHIFT",
    VOICEMAIL_TRANSCRIPTION = "VOICEMAIL_TRANSCRIPTION",
    TO_DO_LIST_CATEGORIES = "TO_DO_LIST_CATEGORIES",
    COMPANY_SPLASH_SCREEN_MODIFICATION = "COMPANY_SPLASH_SCREEN_MODIFICATION",
    TRANSCRIPT_POST_MEETING = "TRANSCRIPT_POST_MEETING",
    CLOUD_PBX_BASIC_CALL_CENTER = "CLOUD_PBX_BASIC_CALL_CENTER",
    CLOUD_PBX_REJECT_CALL = "CLOUD_PBX_REJECT_CALL",
    IA_BYOIA = "IA_BYOIA",
    AI_BY_R_TRANSCRIPT_POST_MEETING = "AI_BY_R_TRANSCRIPT_POST_MEETING",
    LIVE_CAPTION = "LIVE_CAPTION"
}

export class ProfileService extends Service {

    // TODO : Create a true enum...
    public FeaturesEnum = {
        COMPANY_ADMIN_COUNT: "COMPANY_ADMIN_COUNT",
        COMPANY_LOGO_MODIFICATION: "COMPANY_LOGO_MODIFICATION",
        COMPANY_DOMAIN_NAME_MODIFICATION: "COMPANY_DOMAIN_NAME_MODIFICATION",
        COMPANY_DETAILS_MODIFICATION: "COMPANY_DETAILS_MODIFICATION",
        COMPANY_SINGLE_SIGN_ON_SAML: "COMPANY_SINGLE_SIGN_ON_SAML",
        COMPANY_SINGLE_SIGN_ON_OIDC: "COMPANY_SINGLE_SIGN_ON_OIDC",
        SECURITY_MFA_TOTP: "SECURITY_MFA_TOTP",
        WEBRTC_FOR_MOBILE: "WEBRTC_FOR_MOBILE",
        BUBBLE_PARTICIPANT_COUNT: "BUBBLE_PARTICIPANT_COUNT",
        TELEPHONY_BASIC_CALL: "TELEPHONY_BASIC_CALL",
        TELEPHONY_SECOND_CALL: "TELEPHONY_SECOND_CALL",
        TELEPHONY_TRANSFER_CALL: "TELEPHONY_TRANSFER_CALL",
        TELEPHONY_CONFERENCE_CALL: "TELEPHONY_CONFERENCE_CALL",
        TELEPHONY_DEFLECT_CALL: "TELEPHONY_DEFLECT_CALL",
        TELEPHONY_PHONE_BOOK: "TELEPHONY_PHONE_BOOK",
        TELEPHONY_VOICE_MAIL: "TELEPHONY_VOICE_MAIL",
        TELEPHONY_CALL_FORWARD: "TELEPHONY_CALL_FORWARD",
        TELEPHONY_NOMADIC: "TELEPHONY_NOMADIC",
        CONFERENCE_PARTICIPANT_COUNT: "CONFERENCE_PARTICIPANT_COUNT",
        WEBRTC_CONFERENCE_ALLOWED: "WEBRTC_CONFERENCE_ALLOWED",
        WEBRTC_CONFERENCE_PARTICIPANT_COUNT: "WEBRTC_CONFERENCE_PARTICIPANT_COUNT",
        WEBRTC_PARTICIPANT_ALLOWED: "WEBRTC_PARTICIPANT_ALLOWED",
        CONFERENCE_ALLOWED: "CONFERENCE_ALLOWED",
        CONFERENCE_DIAL_OUT: "CONFERENCE_DIAL_OUT",
        CONFERENCE_RECORDING: "CONFERENCE_RECORDING",
        MSO365_CALENDAR_PRESENCE: "MSO365_CALENDAR_PRESENCE",
        MSO365_DIRECTORY_PROVISIONING: "MSO365_DIRECTORY_PROVISIONING",
        MSO365_DIRECTORY_SEARCH: "MSO365_DIRECTORY_SEARCH",
        MSO365_CONTACT_SEARCH: "MSO365_CONTACT_SEARCH",
        MS_OUTLOOK_PLUGIN: "MS_OUTLOOK_PLUGIN",
        MS_SKYPE_PLUGIN: "MS_SKYPE_PLUGIN",
        FILE_SHARING_QUOTA_GB: "FILE_SHARING_QUOTA_GB",
        GOOGLE_CALENDAR_PRESENCE: "GOOGLE_CALENDAR_PRESENCE",
        WEBRTC_P2P_RECORDING: "WEBRTC_P2P_RECORDING",
        BUBBLE_PROMOTE_MEMBER: "BUBBLE_PROMOTE_MEMBER",
        BUBBLE_GUESTS_ALLOWED: "BUBBLE_GUESTS_ALLOWED",
        BUBBLE_ACTIVE_SPEAKER: "BUBBLE_ACTIVE_SPEAKER",
        TELEPHONY_WEBRTC_GATEWAY: "TELEPHONY_WEBRTC_GATEWAY",
        TELEPHONY_WEBRTC_PSTN_CALLING: "TELEPHONY_WEBRTC_PSTN_CALLING",
        ANALYTICS_DASHBOARD_EC: "ANALYTICS_DASHBOARD_EC",
        ANALYTICS_DASHBOARD_BP: "ANALYTICS_DASHBOARD_BP",
        TELEPHONY_CALL_SUBJECT: "CALL_SUBJECT",
        CHANNEL_CREATE: "CHANNEL_CREATE",
        CHANNEL_CREATE_ADMIN_ROLE_BYPASS: "CHANNEL_CREATE_ADMIN_ROLE_BYPASS",
        CHANNEL_ACTIVATED: "CHANNEL_ACTIVATED",
        MANAGE_ANDROID_TV: "MANAGE_ANDROID_TV",
        USE_ANDROID_TV: "USE_ANDROID_TV",
        LDAP_DIRECTORY_SYNC: "LDAP_DIRECTORY_SYNC",
        LDAP_CONTACT_SYNC: "LDAP_CONTACT_SYNC",
        MASSPRO_USER_PROVISIONING_CSV: "MASSPRO_USER_PROVISIONING_CSV",
        SEARCH_BY_TAGS: "SEARCH_BY_TAGS",
        TELEPHONY_3DPARTY_ASSOC: "TELEPHONY_3DPARTY_ASSOC",
        WEBCLIENT_UI_2_0: "WEBCLIENT_UI_2_0",
        WEBRTC_UNIFIED_PLAN: "WEBRTC_UNIFIED_PLAN",
        WEBRTC_CONFERENCE_SIMULCAST: "WEBRTC_CONFERENCE_SIMULCAST",
        USE_CLOUD_PBX: "USE_CLOUD_PBX",
        USE_OXO_MANAGED_PBX: "USE_OXO_MANAGED_PBX",
        SOFTPHONE_MODE_ONLY: "SOFTPHONE_MODE_ONLY",
        GIVE_REQUEST_REMOTE_CONTROL: "GIVE_REQUEST_REMOTE_CONTROL",
        BUBBLE_MAX_LOAD_STARTUP: "BUBBLE_MAX_LOAD_STARTUP",
        BUBBLE_COUNT: "BUBBLE_COUNT",
        BUBBLE_MEETING_SCHEDULER: "BUBBLE_MEETING_SCHEDULER",
        BUBBLE_TALKING_TIME: "BUBBLE_TALKING_TIME",
        APP_PERMISSIONS: "APP_PERMISSIONS",
        FREE_UI_CUSTOMIZATION: "FREE_UI_CUSTOMIZATION",
        PREMIUM_UI_CUSTOMIZATION: "PREMIUM_UI_CUSTOMIZATION",
        TELEPHONY_MAKE_CALL_CTI_APP: "TELEPHONY_MAKE_CALL_CTI_APP",
        CLOUD_PBX_WELCOME: "CLOUD_PBX_WELCOME",
        BUBBLE_TAG_COUNT: "BUBBLE_TAG_COUNT",
        TELEPHONY_CCD_AGENT: "TELEPHONY_CCD_AGENT",
        ALERT_NOTIFICATIONS_ALLOWED: "ALERT_NOTIFICATIONS_ALLOWED",
        BUBBLE_FOLDER_COUNT: "BUBBLE_FOLDER_COUNT",
        BUBBLE_POP_OUT_VIDEO_SHARING: "BUBBLE_POP_OUT_VIDEO_SHARING",
        ACCESSIBILITY_KEYBOARD_SHORTCUTS: "ACCESSIBILITY_KEYBOARD_SHORTCUTS",
        WEBRTC_ADD_PARTICIPANT: "WEBRTC_ADD_PARTICIPANT",
        WEBINAR_ACCESS: "WEBINAR_ACCESS",
        WEBINAR_MAX_ORGANIZERS: "WEBINAR_MAX_ORGANIZERS",
        WEBINAR_MAX_PARTICIPANTS: "WEBINAR_MAX_PARTICIPANTS",
        WEBINAR_MAX_ONSTAGE: "WEBINAR_MAX_ONSTAGE",
        WEBINAR_EXTENDED_BACKSTAGE: "WEBINAR_EXTENDED_BACKSTAGE",
        BLURRING_UI_2_0: "BLURRING_UI_2_0",
        TELEPHONY_MAX_KEYS: "TELEPHONY_MAX_KEYS",
        TELEPHONY_MAX_SPEED_DIALING_KEYS: "TELEPHONY_MAX_SPEED_DIALING_KEYS",
        TELEPHONY_MAX_FEATURE_CODE_KEYS: "TELEPHONY_MAX_FEATURE_CODE_KEYS",
        TELEPHONY_MAX_SUPERVISION_KEYS: "TELEPHONY_MAX_SUPERVISION_KEYS",
        CONNECTIVITY_ALL_USER: "CONNECTIVITY_ALL_USER",
        WEBRTC_BLIND_TRANSFER_CALL: "WEBRTC_BLIND_TRANSFER_CALL",
        TELEPHONY_ADD_PARTICIPANT: "TELEPHONY_ADD_PARTICIPANT",
        TELEPHONY_BASIC_ATTENDANT_CONSOLE: "TELEPHONY_BASIC_ATTENDANT_CONSOLE",
        TELEPHONY_HYBRID_ATTENDANT_CONSOLE: "TELEPHONY_HYBRID_ATTENDANT_CONSOLE",
        MIX_WEBRTC_PSTN_CALLS: "MIX_WEBRTC_PSTN_CALLS",
        CLOUD_PBX_PERSONAL_ROUTINES: "CLOUD_PBX_PERSONAL_ROUTINES",
        TELEPHONY_MULTI_WEBRTC_GATEWAY: "TELEPHONY_MULTI_WEBRTC_GATEWAY",
        WEBRTC_GRID49: "WEBRTC_GRID49",
        WEBRTC_CONFERENCE_V2_MODEL: "WEBRTC_CONFERENCE_V2_MODEL",
        TELEPHONY_OXO_MULTICALL: "TELEPHONY_OXO_MULTICALL",
        WEBRTC_MODULATE_VIDEO_QUALITY: "WEBRTC_MODULATE_VIDEO_QUALITY",
        ADMIN_ALARMS: "ADMIN_ALARMS",
        FLEXIBLE_PANEL: "FLEXIBLE_PANEL",
        BUBBLE_PSTN_DIALIN: "BUBBLE_PSTN_DIALIN",
        CONFERENCE_RECORDING_MAX_TIMER: "CONFERENCE_RECORDING_MAX_TIMER",
        PERSONAL_DIRECTORY: "PERSONAL_DIRECTORY",
        MSO365_TEAMS_PRESENCE: "MSO365_TEAMS_PRESENCE",
        ELEARNING_TRAINING: "ELEARNING_TRAINING",
        REPORT_A_PROBLEM: "REPORT_A_PROBLEM",
        WEBRTC_MANAGE_TWO_CALLS: "WEBRTC_MANAGE_TWO_CALLS",
        SUPERVISION_GROUP_MAX_NUMBER: "SUPERVISION_GROUP_MAX_NUMBER",
        SUPERVISION_GROUP_MAX_USERS: "SUPERVISION_GROUP_MAX_USERS",
        SUPERVISION_GROUP_SUPERVISOR_ALLOWED: "SUPERVISION_GROUP_SUPERVISOR_ALLOWED",
        SUPERVISION_GROUP_IN_OUT_ALLOWED: "SUPERVISION_GROUP_IN_OUT_ALLOWED",
        SUPERVISION_GROUP_SHOW_CONSOLE: "SUPERVISION_GROUP_SHOW_CONSOLE",
        TELEPHONY_CALL_POPOUT: "TELEPHONY_CALL_POPOUT",
        TELEPHONY_OXE_EMERGENCY: "TELEPHONY_OXE_EMERGENCY",
        MESSAGE_BOOKMARK: "MESSAGE_BOOKMARK",
        CLOUD_PBX_CALL_QUEUE_MAX_USERS: "CLOUD_PBX_CALL_QUEUE_MAX_USERS",
        CLOUD_PBX_MANAGER_ASSISTANT: "CLOUD_PBX_MANAGER_ASSISTANT",
        TELEPHONY_JOIN_BUBBLE: "TELEPHONY_JOIN_BUBBLE",
        TO_DO_LIST_MAX_ITEMS: "TO_DO_LIST_MAX_ITEMS",
        RED_ACCESS: "RED_ACCESS",
        COMPANY_DIRECTORY_INTERNAL_NUM: "COMPANY_DIRECTORY_INTERNAL_NUM",
        COMPANY_DIRECTORY_SHORTCODE: "COMPANY_DIRECTORY_SHORTCODE",
        DISABLED_VIDEO_RECEIVING: "DISABLED_VIDEO_RECEIVING",
        UNMUTE_REQUEST: "UNMUTE_REQUEST",
        CLOUD_PBX_GROUP_CALL_LOG: "CLOUD_PBX_GROUP_CALL_LOG",
        TELEPHONY_SOFTPHONE_NOT_ALLOWED: "TELEPHONY_SOFTPHONE_NOT_ALLOWED",
        TELEPHONY_OXE_OVERFLOW: "TELEPHONY_OXE_OVERFLOW",
        FILE_SHARING_EXTERNAL_OWNCLOUD: "FILE_SHARING_EXTERNAL_OWNCLOUD",
        AUDIO_NOISE_REDUCTION: "AUDIO_NOISE_REDUCTION",
        MIX_WEBRTC_PSTN_CALLS_FOR_PBX: "MIX_WEBRTC_PSTN_CALLS_FOR_PBX",
        WEBRTC_CALL_SHIFT: "WEBRTC_CALL_SHIFT",
        TELEPHONY_OXE_SECRET: "TELEPHONY_OXE_SECRET",
        TELEPHONY_SOFTPHONE_V3: "TELEPHONY_SOFTPHONE_V3",
        TELEPHONY_SOFTPHONE_V3_UC: "TELEPHONY_SOFTPHONE_V3_UC",
        CLOUD_PBX_CALL_QUEUE_RECORDING: "CLOUD_PBX_CALL_QUEUE_RECORDING",
        OPEN_XCHANGE_CALENDAR_PRESENCE: "OPEN_XCHANGE_CALENDAR_PRESENCE",
        HYBRID_MANAGER_ASSISTANT: "HYBRID_MANAGER_ASSISTANT",
        BLUE_GREEN_WEB: "BLUE_GREEN_WEB",
        BUBBLE_POLLING: "BUBBLE_POLLING",
        RAISE_HAND_CONFERENCE: "RAISE_HAND_CONFERENCE",
        PROMOTE_ALL: "PROMOTE_ALL",
        TELEPHONY_MAX_CALL: "TELEPHONY_MAX_CALL",
        DESKTOP_EXTEND_BUTTON: "DESKTOP_EXTEND_BUTTON",
        CALL_DURING_BUBBLE: "CALL_DURING_BUBBLE",
        CONFERENCE_ATTENDANCE: "CONFERENCE_ATTENDANCE",
        PIN_MESSAGES: "PIN_MESSAGES",
        PINS_ITEMS_COUNT_PER_CONVERSATION: "PINS_ITEMS_COUNT_PER_CONVERSATION",
        TELEPHONY_CALL_BUSY: "TELEPHONY_CALL_BUSY",
        LOWER_ALL_HANDS: "LOWER_ALL_HANDS",
        CLOUD_PBX_DECT_ZERO_TOUCH: "CLOUD_PBX_DECT_ZERO_TOUCH",
        CLOUD_PBX_PHONES_GETLOGS: "CLOUD_PBX_PHONES_GETLOGS",
        SECURITY_PASSWORDLESS: "SECURITY_PASSWORDLESS",
        CLOUD_PBX_PHONES_GROUP_WITHDRAW: "CLOUD_PBX_PHONES_GROUP_WITHDRAW",
        BUBBLE_WAITING_ROOM: "BUBBLE_WAITING_ROOM",
        BLURRING_UI_3_0: "BLURRING_UI_3_0",
        MASK_COMPANY_SEARCH: "MASK_COMPANY_SEARCH",
        CLEAR_CHAT: "CLEAR_CHAT",
        TELEPHONY_OXE_EMERGENCY_SITE: "TELEPHONY_OXE_EMERGENCY_SITE",
        TELEPHONY_CCD_AGENT_PROFILE: "TELEPHONY_CCD_AGENT_PROFILE",
        TELEPHONY_HYBRID_SOFTPHONE_CALL_SHIFT: "TELEPHONY_HYBRID_SOFTPHONE_CALL_SHIFT",
        VOICEMAIL_TRANSCRIPTION: "VOICEMAIL_TRANSCRIPTION",
        TO_DO_LIST_CATEGORIES: "TO_DO_LIST_CATEGORIES",
        COMPANY_SPLASH_SCREEN_MODIFICATION: "COMPANY_SPLASH_SCREEN_MODIFICATION",
        CUSTOMIZE_STATUS: "CUSTOMIZE_STATUS",
        TRANSCRIPT_POST_MEETING: "TRANSCRIPT_POST_MEETING",
        REACT_TO_MESSAGES: "REACT_TO_MESSAGES",
        TEXT_FORMATTING: "TEXT_FORMATTING",
        TELEPHONY_3DPARTY_WEBRTC_PSTN_CALLING: "TELEPHONY_3DPARTY_WEBRTC_PSTN_CALLING",
        CLOUD_PBX_BASIC_CALL_CENTER: "CLOUD_PBX_BASIC_CALL_CENTER",
        CLOUD_PBX_REJECT_CALL: "CLOUD_PBX_REJECT_CALL",
        IA_BYOIA: "IA_BYOIA",
        AI_BY_R_TRANSCRIPT_POST_MEETING: "AI_BY_R_TRANSCRIPT_POST_MEETING",
        LIVE_CAPTION: "LIVE_CAPTION"
    } satisfies Record<string, string>;

    public rxSubject: Subject<any> = new Subject<any>();
    public telsoftPhoneNotAllowed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);//RQRAINB-8921++

    /**
     * @public
     * This RB event is send when the features list of the logged user has changed.
     */
    public static PROFILE_FEATURES_UPDATE = 'ON_PROFILE_FEATURES_UPDATED';
    public static PROFILE_TELEPHONY_SOFTPHONE_NOT_ALLOWED = 'ON_TELEPHONY_SOFTPHONE_NOT_ALLOWED';

    private started: boolean = false;
    private features: any = {};
    private profiles: any[] = [];
    private mainOffers: Offer[] = [];
    private thirdPartyApps: any = null;
    private sdkApiKeys: SdkApiKey[] = undefined;
    private profileEventHandler: any = null;
    private timer: any = null;
    private contactService: ContactService = ContactService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();

    /* ******************************************************************* */
    /* * LIFECYCLE STUFF                                                 * */
    /* ******************************************************************* */
    public static getInstance() {
        return Service.create(PROFILE_SVC, ProfileService);
    }

    public static build() {
        return new ProfileService();
    }

    private constructor() { super(); }

    public async start() {
        try {
            this.logger.info("");
            this.logger.info("[profileService] === STARTING ===");
            this.attachHandlers();

            await this.getServerProfile();
            if (this.contactService?.userContact?.canUseTranscriptionCustomisation) {
                this.getAICapabilities();
            }
            this.started = true;
            this.logger.info("[profileService] === STARTED ===");
            this.rxSubject.next(RBEvent.create(ProfileService.PROFILE_FEATURES_UPDATE));
        }
        catch (error) {
            this.logger.info(`[profileService] === STARTING FAILURE === ${error.message}`);
            throw error;
        }
    }

    public async stop() {
        this.logger.info("[profileService] === STOPPING ===");
        this.detachHandlers();
        this.started = false;
        this.thirdPartyApps = null;
        this.sdkApiKeys = undefined;
        this.logger.info("[profileService] === STOPPED ===");
    }

    public reconnect(): void {
        this.restart();
    }

    public restart(): void {
        this.logger.info("[profileService] === RESTART ===");
        this.attachHandlers();
        this.onUserUpdateNeeded();
    }

    public subscribe(handler: any): Subscription { return this.rxSubject.subscribe(handler); }

    private attachHandlers(): void {
        this.detachHandlers();
        this.logger.info("[profileService] attachHandlers");
        this.profileEventHandler = this.xmppService.addHandler((stanza: any) => { return this.onManagementMessageReceived(stanza); }, null, "message", "management");
    }

    private detachHandlers(): void {
        this.logger.info("[profileService] detachHandlers");
        if (this.profileEventHandler) { this.xmppService.deleteHandler(this.profileEventHandler); this.profileEventHandler = null; }
    }

    private onManagementMessageReceived(stanza: any): boolean {
        try {
            const userprofileElem = xmlParser(stanza).find("userprofile");
            if (userprofileElem.length) {
                this.logger.info("[profileService] onManagementMessageReceived  -- on userprofile management message received");

                switch (userprofileElem.attr("action")) {
                    case "create":
                    case "update":
                    case "delete":
                        this.onUserUpdateNeeded();
                        break;
                    default: this.logger.info("[profileService] onManagementMessageReceived  -- unknown action"); break;
                }
            }
            return true;
        }
        catch (error) {
            this.logger.error(`[profileService] onManagementMessageReceived ERROR ${error}`);
            return true;
        }
    }

    private onUserUpdateNeeded(): void {
        // Wait 3 seconds before updating our userContact and requesting the featureProfile for this user; ignore events in the meantime

        //For guardian we need to check here and propose a reload of the client to take into account all new updates on the feature
        if (this.timer) { return; }
        this.timer = setTimeout(async () => {
            try {
                await this.contactService.updateContactFromServer(this.contactService.userContact);
                await this.getServerProfile();
                this.rxSubject.next(RBEvent.create(ProfileService.PROFILE_FEATURES_UPDATE));
                this.timer = null;
            }
            catch (error) {
                this.timer = null;
                this.logger.info(`[profileService] === onUserUpdateNeeded FAILURE === ${error.message}`);
            }
        }, 3000);
    }

    /* ****************************************************************** */
    /* * PROFILE API STUFF                                              * */
    /* ****************************************************************** */
    private getServerProfile(): Promise<any> {
        return Promise.all([this.getServerProfiles(), this.getServerProfilesFeatures()]);
    }

    private async getServerProfiles() {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/profiles`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();

            this.profiles = [];
            this.mainOffers = [];
            responseData.data.forEach((profileData: any) => {
                this.logger.debug(`[profileService] getServerProfiles === response === ${profileData}`);
                this.profiles.push(profileData);
                const offer = Offer.createFromProfileData(profileData);
                if (offer.isExclusive || offer.isDefault) { this.mainOffers.push(offer); }
            });
            this.mainOffers.sort(Offer.offerComparator);
        }
        catch (error) {
            let errorMessage = "getServerProfiles failure: no server response";
            if (error) { errorMessage = "getServerProfiles failure: " + JSON.stringify(error); }
            this.logger.error("[profileService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    private async getServerProfilesFeatures() {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/profiles/features`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            this.features = {};
            responseData.data.forEach((featureData: any) => {
                this.logger.debug(`[profileService] getServerProfilesFeatures === ${featureData.featureUniqueRef}${featureData.featureType === 'boolean' ? ' isEnabled:' + featureData.isEnabled : ''}${featureData.featureType === 'number' && featureData['limitMin'] ? 'limitMin:' + featureData.limitMin + ' ' : ' '}${featureData.featureType === 'number' && featureData['limitMax'] ? 'limitMax:' + featureData.limitMax : ''}`);
                if (featureData.hasOwnProperty("featureUniqueRef")) {
                    this.features[featureData.featureUniqueRef] = featureData;
                }
            });

            //RQRAINB-8921++
            if (this.features[this.FeaturesEnum.TELEPHONY_SOFTPHONE_NOT_ALLOWED]?.isEnabled) {
                this.logger.debug(`[profileService] getServerProfilesFeatures === TELEPHONY_SOFTPHONE_NOT_ALLOWED => LOGOUT`);
                this.telsoftPhoneNotAllowed.next(true);
            }//RQRAINB-8921--

            const userContact = this.contactService.userContact;
            userContact.attendantConsoleEnabled
                // voice attendant
                = (userContact.pbxId && userContact.isRainbowHubUser()
                    && ((this.features.hasOwnProperty(this.FeaturesEnum.TELEPHONY_BASIC_ATTENDANT_CONSOLE)
                        && this.features[this.FeaturesEnum.TELEPHONY_BASIC_ATTENDANT_CONSOLE].isEnabled)
                        || config[this.FeaturesEnum.TELEPHONY_BASIC_ATTENDANT_CONSOLE]))
                // hybrid attendant
                || (userContact.pbxId && !userContact.isRainbowHubUser()
                    && ((this.features.hasOwnProperty(this.FeaturesEnum.TELEPHONY_HYBRID_ATTENDANT_CONSOLE)
                        && this.features[this.FeaturesEnum.TELEPHONY_HYBRID_ATTENDANT_CONSOLE].isEnabled)
                        || config[this.FeaturesEnum.TELEPHONY_HYBRID_ATTENDANT_CONSOLE]));

            userContact.supervisorConsoleEnabled = userContact.pbxId && userContact.isSupervisor()
                && ((this.features.hasOwnProperty(this.FeaturesEnum.SUPERVISION_GROUP_SHOW_CONSOLE)
                    && this.features[this.FeaturesEnum.SUPERVISION_GROUP_SHOW_CONSOLE].isEnabled)
                    || config[this.FeaturesEnum.SUPERVISION_GROUP_SHOW_CONSOLE]);

            userContact.generalizedAttendantLookForUsers = !userContact.attendantConsoleEnabled && userContact.pbxId && userContact.isRainbowHubUser()
            && this.features.hasOwnProperty(this.FeaturesEnum.TELEPHONY_MAX_CALL) && this.features[this.FeaturesEnum.TELEPHONY_MAX_CALL]?.limitMax > 2; //RQRAINB-9428
            this.logger.debug(`[profileService] getServerProfilesFeatures / generalizedAttendantLookForUsers: ` + userContact.generalizedAttendantLookForUsers + ' TELEPHONY_MAX_CALL = ' + this.features[this.FeaturesEnum.TELEPHONY_MAX_CALL]?.limitMax);
        }
        catch (error) {
            let errorMessage = "getServerProfilesFeatures failure: no server response";
            if (error) { errorMessage = `getServerProfilesFeatures failure: ${JSON.stringify(error)}`; }
            this.logger.error("[profileService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /*
    * APIs for GUI components
    * Used by SDK (public)
    * Warning when modifying this method
    */
    public isFeatureEnabled(featureUniqueRef: string, features: any = null): boolean {
        if (!features) { features = this.features; }

        if (config[featureUniqueRef] !== undefined) return config[featureUniqueRef]; // to test features in dev

        if (this.started
            && features.hasOwnProperty(featureUniqueRef)
            && features[featureUniqueRef].hasOwnProperty("featureType")
            && features[featureUniqueRef].featureType === "boolean"
            && features[featureUniqueRef].hasOwnProperty("isEnabled")) {
            return features[featureUniqueRef].isEnabled;
        }
        return false;
    }

    public getFeatureLimitMax(featureUniqueRef: string, features: any = null): number {
        if (!features) { features = this.features; }

        if (config[featureUniqueRef] && Number.isInteger(config[featureUniqueRef])) return config[featureUniqueRef]; // to test features in dev

        if (this.started
            && features.hasOwnProperty(featureUniqueRef)
            && features[featureUniqueRef].hasOwnProperty("featureType")
            && features[featureUniqueRef].featureType === "number"
            && features[featureUniqueRef].hasOwnProperty("limitMax")) {
            const limitMax = features[featureUniqueRef].limitMax;
            return limitMax;
        }
        return 0;
    }

    public getFeatureLimitMin(featureUniqueRef: string, features: any = null): number {
        if (!features) { features = this.features; }

        if (this.started
            && features.hasOwnProperty(featureUniqueRef)
            && features[featureUniqueRef].hasOwnProperty("featureType")
            && features[featureUniqueRef].featureType === "number"
            && features[featureUniqueRef].hasOwnProperty("limitMin")) {
            const limitMin = features[featureUniqueRef].limitMin;
            return limitMin;
        }
        return 0;
    }

    /*
    * APIs for GUI components
    * Used by SDK (public)
    */
    public getMyProfiles(): any[] {
        let profiles = [];
        if (this.started) { profiles = this.profiles; }
        else { this.logger.warn("[profileService] getMyProfiles(): service not started"); }
        return profiles;
    }

    /*
    * Used by SDK (public)
    * Warning when modifying this method
    */
    public getMyProfileFeatures(): any {
        const profileFeatures = {};
        if (this.started) {
            // Return a simplified feature object with featureType, limitMin, limitMax and isEnabled properties only
            Object.keys(this.features).forEach((featureUniqueRef) => {
                const originalFeature = this.features[featureUniqueRef];
                const feature = {};
                Object.keys(originalFeature).filter((featureProperty) => {
                    return (featureProperty === "featureUniqueRef" || featureProperty === "featureType" || featureProperty === "limitMin" || featureProperty === "limitMax" || featureProperty === "isEnabled");
                }).forEach((featureProperty) => { feature[featureProperty] = originalFeature[featureProperty]; });
                profileFeatures[featureUniqueRef] = feature;
            });
        }
        else { this.logger.warn("[profileService] getMyProfileFeatures(): service not started"); }
        return profileFeatures;
    }

    /*
    * Get The list of the Third Party Application that have access to this Rainbow Account
    * Parameter force in order to refresh the list
    */
    public async getThirdPartyApps(force: boolean = false) {
        try {
            // We've already asked the server for the list
            if (this.thirdPartyApps !== null && !force) {
                this.logger.info("[profileService] getThirdPartyApps -- from cache");
                return this.thirdPartyApps;
            }

            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/oauth/tokens?format=medium`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();

            this.logger.info("[profileService] getThirdPartyApps from server -- success");
            this.thirdPartyApps = (responseData?.data) ? responseData.data : [];
            return this.thirdPartyApps;
        }
        catch (error) {
            let errorMessage = "getThirdPartyApps from server failed -- no answer from server";
            if (error) { errorMessage = "getThirdPartyApps from server failed -- " + JSON.stringify(error); }
            this.logger.error("[profileService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /*
    * Revoke the access of a third-party application from Rainbow
    * The tokenId should be sent as a parameter
    */
    public async revokeThirdPartyAccess(tokenId: string) {
        try {
            if (!tokenId) {
                this.logger.warn("[profileService] revokeThirdPartyAccess missing token");
                throw new Error('No tokenId');
            }
            this.logger.info("[profileService] revokeThirdPartyAccess with token -- " + tokenId);

            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/oauth/tokens/${tokenId}`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            this.logger.info("[profileService] revokeThirdPartyAccess -- success");
            if (!this.thirdPartyApps) {
                this.thirdPartyApps = (responseData?.data) ? responseData.data : [];
            }
            this.thirdPartyApps.forEach((app: any, index: number) => {
                if (app.id === tokenId) { this.thirdPartyApps.splice(index, 1); }
            });
            return this.thirdPartyApps;
        }
        catch (error) {
            let errorMessage = "revokeThirdPartyAccess from server failed -- no answer from server";
            if (error) { errorMessage = "revokeThirdPartyAccess from server failed -- " + JSON.stringify(error); }
            this.logger.error("[profileService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    public getProfilesByName(name: string): any {
        return this.profiles.filter((profile) => { return profile.profileName.toLowerCase().indexOf(name) !== -1; });
    }

    //API KEYS
    //for more info, https://git.openrainbow.org/rainbow-wiki/Wiki/-/wikis/Authentication-with-API_KEY

    /**
    * This API allow a user to get all his API_KEYs, or an administrator to get all API_KEYs of a user that he's responsible
    */
    public async getAllSdkApiKeys(): Promise<SdkApiKey[]> {
        try {
            // We've already asked the server for the list
            if (this.sdkApiKeys) {
                this.logger.info("[profileService] getAllApiKeys -- from cache");
                return this.sdkApiKeys;
            }

            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/apikeys?format=medium`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();

            this.logger.info("[profileService] getAllApiKeys from server -- success");
            this.sdkApiKeys = (responseData?.data) ? responseData.data : [];
            return this.sdkApiKeys;
        }
        catch (error) {
            let errorMessage = "getAllApiKeys from server failed -- no answer from server";
            if (error) { errorMessage = "getAllApiKeys from server failed -- " + JSON.stringify(error); }
            this.logger.error("[profileService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
    * Create a new SDK Api Key
    * @param description - the description of the key
    * @param expirationDate - the date of the expiration or null for never expires
    * Returns the list of the all SDK Api Keys
    */
    public async createSdkApiKey(description: string, expirationDate: Date | null): Promise<SdkApiKey> {
        try {
            if (!description) {
                this.logger.warn("[profileService] createSdkApiKey missing data");
                throw new Error('Missing data');
            }
            this.logger.info("[profileService] createSdkApiKey");
            const data = { description, scope: ['all'], isActive: true };
            if (expirationDate) data['expirationDate'] = expirationDate;

            const body = JSON.stringify(data);
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/apikeys`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            this.logger.info("[profileService] createSdkApiKey -- success");
            if (!this.sdkApiKeys) this.sdkApiKeys = [];
            this.sdkApiKeys.push(responseData.data);

            //return the last created key
            return this.sdkApiKeys[this.sdkApiKeys.length - 1];
        }
        catch (error) {
            let errorMessage = "createSdkApiKey from server failed -- no answer from server";
            if (error) { errorMessage = "createSdkApiKey from server failed -- " + JSON.stringify(error); }
            this.logger.error("[profileService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
    * Deletes a SDK Api Key
    * @param sdkApiKey - the key to delete
    * Returns the list of the remaining SDK Api Keys
    */
    public async deleteSdkApiKey(sdkApiKey: SdkApiKey): Promise<SdkApiKey[]> {
        try {
            if (!sdkApiKey?.id) {
                this.logger.warn("[profileService] deleteSdkApiKey missing data");
                throw new Error('Missing data');
            }
            this.logger.info("[profileService] deleteSdkApiKey");
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/apikeys/${sdkApiKey.id}`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            this.logger.info("[profileService] deleteSdkApiKey -- success");
            if (!this.sdkApiKeys) {
                this.sdkApiKeys = (responseData?.data) ? responseData.data : [];
            }
            this.sdkApiKeys.forEach((app: SdkApiKey, index: number) => {
                if (app.id === sdkApiKey.id) { this.sdkApiKeys.splice(index, 1); }
            });
            return this.sdkApiKeys;
        }
        catch (error) {
            let errorMessage = "deleteSdkApiKey from server failed -- no answer from server";
            if (error) { errorMessage = "deleteSdkApiKey from server failed -- " + JSON.stringify(error); }
            this.logger.error("[profileService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    private async getAICapabilities() {
        try {
            const url = `${config.restServerUrl}/api/rainbow/aihub/v1.0/enduser/capabilities`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();

            this.contactService.userContact.recordingTranscription = responseData.data.recordingTranscription;
            this.contactService.userContact.liveTranscription = responseData.data.liveTranscription;
            this.contactService.userContact.translateDoc = responseData.data.translateDoc;
            if (responseData.data.translateDoc) {
                this.contactService.userContact.translationDocLanguages = responseData.data.translationDocLanguages;
                this.contactService.userContact.translationDocFormats = responseData.data.translationDocFormats;
            }
            if (responseData.data.recordingTranscription) {
                this.contactService.userContact.recordingLanguages = responseData.data.recordingLanguages;
            }
            if (responseData.data.liveTranscription) {
                this.contactService.userContact.liveLanguages = responseData.data.liveLanguages;
            }
        }
        catch (error) {
            let errorMessage = "getAICapabilities failure: no server response";
            if (error) { errorMessage = "getAICapabilities failure: " + JSON.stringify(error); }
            this.logger.error("[profileService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

}
